import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateWidgetProperty, toggleEditMode } from "actions/editor";
import { addFieldToContentGrid, fetchMappingsForContentGrid, removeWidgetFromContentGrid, changeContentGridCard, renderItemsForContentGrid } from 'actions/contentGrid';
import { fetchContentCards, invalidateContentCards } from 'actions/contentCards';

import _, { filter, get } from "lodash";
import cx from "classnames";
import { Frame } from "scenejs";
import Truncate from "react-truncate";
import { IKImage } from "imagekitio-react";
import {Button, Modal, Form} from 'react-bootstrap'

import Tooltip from "components/Tooltip";
import { Widget } from "../core";
import { EditorContext } from "components/Editor";
import { Property } from "../../components/properties";
import { GridView, MetroView } from "components/Editor/widgets/content/layouts";
import { useWidgetChildrenSelector, usePageWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { getBoundingRectFromLayout } from 'components/Editor/helper/layoutManager';
import { Background } from 'components/Editor/components/properties/background';
import { BackgroundView } from '../background';
import SectionManager from "./sectionManager";
import DynamicSection from "./sectionManager/dynamicSection";
import { LoadingWidget } from "./loading";
import { updateWidgetEditorState } from "actions/editor";
import { getWidgetType } from "components/Editor/helper/widget";
import { CreateFilter } from "components/Editor/components/properties/createFilter";
import InfiniteScroll from "components/InfiniteScroll";
import { Loader } from "components/ApplicationLoader";
import { Widgets } from "@paperflite-marketing/cleverstory-commons";
import StoryboardContext from "components/Storyboard/context";
import { getBoxShadow } from "services/cssHelper.service";
import FilterContainer, { FilterWidget } from "./filters";

import {
  CONTENT_CARD, CONTENT_CARD_TEMPLATE, CONTENT_FILTER, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING, CONTENT_PAGINATION_BUTTON, CONTENT_FILTER_CONTAINER, CAROUSEL, CAROUSEL_CARD,
  CONTENT,
  SECTION_COLUMN
} from "constants/widgetTypes";
import { fieldsTypes } from 'constants/contentGrid';
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import styles from "./contentGrid.module.scss";

const defaultContentImage = `/cleverstory/images/default-content.png`;
const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

export const ContentWidgetContext = React.createContext({
  contentType: null
})

const ContentWidget = React.memo(function ContentEditor({ className, style, widgetProps, widgetRef, children }) {
  const dispatch = useDispatch();

  const [manual, setManual] = useState(true);
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  const background = _.get(widgetProps, 'properties.desktop.background', null);
  const properties = _.get(widgetProps, `properties.desktop.layoutStyle`, {});
  const desktopProperties = _.get(widgetProps, `properties.desktop`, {});
  const filters = _.get(desktopProperties, 'filters', false);
  const showSearch = _.get(desktopProperties, 'search', false);
  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const contentType = _.get(widgetProps, 'content.type', null);
  const loading = _.get(widgetProps, 'content.mappings.loading');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const contentFilters = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    return widgets.find(item => item.parent === id && item.type === CONTENT_FILTER);
  });

  const showFilter = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const contentFilter = widgets.find(item => item.parent === id && item.type === CONTENT_FILTER);
    const filterContainerWidget = widgets.find(item => item.parent === _.get(contentFilter, 'uid', null) && item.type === CONTENT_FILTER_CONTAINER);
    const content = _.get(filterContainerWidget, 'content', null);

    if (filters && contentFilter && _.size(content) > 0) {
      return true;
    }

    return false;
  })

  const editMode = useSelector((state) => {
    return _.get(state, `${editor}.present.editMode`) === id;
  });

  const childrenWidgets = useWidgetChildrenSelector(editor, id);

  const pagination = _.get(widgetProps, 'properties.desktop.layoutStyle.pagination', 'infinite_scroll');
  const paginationButton = pagination === 'load_more' && childrenWidgets.find(item => _.get(item, 'type') === CONTENT_PAGINATION_BUTTON);
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false) && storyboardType != 'INTERACTIVE_CONTENT';
  const empty = !loading && _.get(widgetProps, 'content.mappings.items.length', 0) === 0;
  
  var contentWidgetStyle = {
    ...style,
    ...getStyles(widgetProps, storyboardType, breakpoint)
  }

  var buttonStyle = getButtonStyle(widgetProps, breakpoint);

  const handleContentManager = (type) => {
    dispatch(toggleEditMode(editor, widgetProps.uid, true));
    setManual(type == 'manual')
  }

  useEffect(() => {
    if(contentType) {
      setManual(contentType == 'MANUAL')
    }
  }, [contentType])

  return (
    <ContentWidgetContext.Provider value={{
      contentType: contentType
    }}>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.content, "widget", {
          [styles.stretched]: stretched,
          [styles.mobile]: breakpoint === 'mobile'
        })}
        style={contentWidgetStyle}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={true}
      >
        <BackgroundView background={background} />

        {(showFilter || showSearch) && 
          <div id="filter_parent" className={styles.filterContainer}>
            <Widget widgetProps={contentFilters} breakpoint={breakpoint} />
          </div>
        }

        <ContentLayoutView widget={widgetProps} />

        {paginationButton &&
          <div style={buttonStyle} className={cx(styles.paginationButton, 'pagination_button_container')}>
            <Widget key={paginationButton.uid} widgetProps={paginationButton} />
          </div>
        }

        {empty && storyboardType === 'RESOURCE_CENTER' &&
          <ResourceCenterEmptyMessage breakpoint={breakpoint} onClick={handleContentManager} />
        }

        {empty && storyboardType !== 'RESOURCE_CENTER' &&
          <div className={styles.empty}>
            <button onClick={() => {
              handleContentManager('manual')
            }}>
              <i className={cx('cleverstory-plus')} />
              <span>Choose Content</span>
            </button>
          </div>
        }

        {editMode && manual && (
          <SectionManager
            show={true}
            widgetProps={widgetProps}
            onClose={() => {
              dispatch(toggleEditMode(editor, widgetProps.uid, false));
              dispatch(renderItemsForContentGrid(editor, widgetProps.uid));
            }}
          />
        )}

        {editMode && !manual && (
          <DynamicSection
            show={true}
            widgetProps={widgetProps}
            onClose={() => {
              dispatch(toggleEditMode(editor, widgetProps.uid, false));
            }}
          />
        )}
      </div>
    </ContentWidgetContext.Provider>
  )
})

const ResourceCenterEmptyMessage = ({breakpoint, onClick }) => {
  return (
    <div className={cx(styles.empty, {[styles.mobileEmptyView]: breakpoint === 'mobile'})}>
      <section className={styles.sectionContainer}>
        <Button variant="primary" onClick={() => {
          onClick('manual')
        }} className={styles.sectionButton}>
          <i className={cx('cleverstory-plus', styles.icon)} />
          Manual Section
        </Button>
        <div className={styles.discription}>
          <div className={styles.points}>
            <i className={cx('cleverstory-tick', styles.checkIcon)} />
            <span className={styles.text}>A curated set of assets so you can pick and choose</span>
          </div>
          <div className={styles.points}>
            <i className={cx('cleverstory-tick', styles.checkIcon)} />
            <span className={styles.text}>Flexibility to structure your assets your way (drag and rearrange)</span>
          </div>
          <div className={styles.points}>
            <i className={cx('cleverstory-close', styles.closeIcon)} />
            <span className={styles.text}>Can get tiresome if you’re adding a lot of assets, say, like a gazillion?</span>
          </div>
        </div>
      </section>
      <section className={styles.sectionContainer}>
        <Button variant="primary" onClick={() => { onClick('dynamic') }} className={styles.sectionButton}>
          <i className={cx('cleverstory-plus', styles.icon)} />
          Dynamic Section
        </Button>
        <div className={styles.discription}>
          <div className={styles.points}>
            <i className={cx('cleverstory-tick', styles.checkIcon)} />
            <span className={styles.text}>Automate Tailor-made for Bulk operations.</span>
          </div>
          <div className={styles.points}>
            <i className={cx('cleverstory-tick', styles.checkIcon)} />
            <span className={styles.text}>Automatic updation based on the Query/Conditions.</span>
          </div>
          <div className={styles.points}>
            <i className={cx('cleverstory-close', styles.closeIcon)} />
            <span className={styles.text}>Cannot rearrange as per user's will. Limited control with Sorting.</span>
          </div>
          <div className={styles.points}>
            <i className={cx('cleverstory-close', styles.closeIcon)} />
            <span className={styles.text}>Changing the content on card will change the asset meta.</span>
          </div>
        </div>
      </section>
    </div>
  )
}

const ContentLayoutView = ({ widget}) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const id = _.get(widget, 'uid');
  const updated = _.get(widget, 'editorState.layout.updated', false);

  const itemsPerPage = _.get(widget, 'properties.desktop.layoutStyle.itemsPerPage', 2);
  const rowSpacing = _.get(widget, 'properties.desktop.layoutStyle.rowSpacing', 10);
  const columnSpacing = _.get(widget, 'properties.desktop.layoutStyle.columnSpacing', 10);
  const alignment = _.get(widget, 'properties.desktop.layoutStyle.alignment') || 'center';
  const layoutManagerId = useSelector(state => _.get(state, `${editorSession}.present.layoutManager.id`) || null);
  const loading = _.get(widget, 'content.mappings.loading');
  const fetched = _.get(widget, 'content.mappings.fetched');
  const empty = _.get(widget, 'content.mappings.items.length', 0) === 0;

  const properties = _.get(widget, `properties.desktop.layoutStyle`, {});
  const editorState = _.get(widget, 'editorState', {});
  const renderState = _.get(editorState, 'renderState', 'default');

  const cardTemplate = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.parent === id && item.type === CONTENT_CARD_TEMPLATE);
  });

  const loadingWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.parent === id && item.type === CONTENT_LOADING);
  });

  const noResultWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.parent === id && item.type === CONTENT_EMPTY_MESSAGE);
  });

  const width = _.get(cardTemplate, `properties.${breakpoint}.layout.width`)

  var widgetChildren = useWidgetChildrenSelector(editorSession, id) || [];
  widgetChildren = widgetChildren.filter(item => {
    return _.get(item, `type`) === CONTENT_CARD;
  });

  var widgetChildrenLength = widgetChildren.length;

  widgetChildren = _.orderBy(widgetChildren, 'order', 'asc');
  widgetChildren = _.slice(widgetChildren, 0, itemsPerPage);

  useEffect(() => {
    if (id || updated ){
      dispatch(fetchMappingsForContentGrid(editorSession, id, 0, itemsPerPage, false));
    }
  }, [id, updated, itemsPerPage])

  return (
    <div className={styles.layoutRender}>

      {renderState === "loading" &&
        <Widget key={loadingWidget.uid} widgetProps={loadingWidget} breakpoint={breakpoint} />
      }

      {renderState == "noResult" &&
        <Widget key={noResultWidget.uid} widgetProps={noResultWidget} breakpoint={breakpoint} />
      }

      {renderState === "default" &&
        <GridView width={width} rowSpacing={rowSpacing} columnSpacing={columnSpacing} alignment={alignment}>
          {widgetChildren.map((item) => {
            return <Widget key={item.uid} widgetProps={item} editable />
          })}

          {loading && empty &&
            <Widgets.Context.Provider value={{
              imageCDN: IMAGE_CDN_ENDPOINT,
              shouldZoom: true,
              selector: `${editorSession}.present`
            }}>
              {_.times(itemsPerPage).map(index => {
                return <Widgets.Widget key={index} widgetProps={cardTemplate} breakpoint={breakpoint} />
              })}
            </Widgets.Context.Provider>
          }

          {!loading && empty &&
            <Widgets.Context.Provider value={{
              imageCDN: IMAGE_CDN_ENDPOINT,
              shouldZoom: true,
              selector: `${editorSession}.present`
            }}>
              {_.times(itemsPerPage).map(index => {
                return <Widgets.Widget key={index} widgetProps={cardTemplate} breakpoint={breakpoint} />
              })}
            </Widgets.Context.Provider>
          }

          {!loading && !empty && widgetChildrenLength < itemsPerPage && 
            <Widgets.Context.Provider value={{
              imageCDN: IMAGE_CDN_ENDPOINT,
              shouldZoom: true,
              selector: `${editorSession}.present`
          }}>
              {_.times(itemsPerPage - widgetChildrenLength).map(index => {
                return <Widgets.Widget key={index} widgetProps={cardTemplate} breakpoint={breakpoint} />
              })}
            </Widgets.Context.Provider>
          }
          

        </GridView>
      }
    </div>
  );
}

const ContentWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const paginationRef = React.useRef(null)

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const empty = _.get(props, 'content.mappings.items.length', 0) === 0;
  const background = _.get(props, 'properties.desktop.background', null);

  const totalPages = _.get(props, 'content.mappings.pagination.totalPages', 1);
  const limitPerPage = _.get(props, 'properties.desktop.layoutStyle.itemsPerPage', 2);
  const totalRecordsCount = _.get(props, 'content.mappings.pagination.totalRecordsCount', 0);
  const currentPage = _.get(props, 'content.mappings.pagination.current', 0);
  const loading = _.get(props, 'content.mappings.loading');
  
  const getPageRanges = () => {
    const pageRanges = [];
    for (let i = 0; i < totalPages; i++) {
      const startItem = i * limitPerPage + 1;
      const endItem = Math.min((i + 1) * limitPerPage, totalRecordsCount);
      const range = `${startItem}-${endItem}`;
      pageRanges.push(range);
    }
    return pageRanges;
  };

  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  if (empty && !loading) {
    return false;
  }

  function onRangeClick(page){
    dispatch(fetchMappingsForContentGrid(editorSession, id, page, limitPerPage, false));
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => {
        dispatch(toggleEditMode(editorSession, props.uid, true));
      }}>Manage Content</Property.Action>

      <Property.Action className={styles.textButton} tooltip='Background' onClick={() => changeView('background')}>
        Change Background
      </Property.Action>

      <Property.Action className={styles.iconButton} tooltip='Fields' onClick={() => changeView('fields')}>
        <i className={cx("cleverstory-lines")} />
      </Property.Action>
 
      <Property.Action className={styles.iconButton} tooltip='Spacing' onClick={() => changeView('spacing')}>
        <i className={cx("cleverstory-spacing")} />
      </Property.Action>

      <Property.Action className={styles.iconButton} tooltip='Grid Options' onClick={() => changeView('grid-options')}>
        <i className={cx("cleverstory-filters")} />
      </Property.Action>

      <Property.Action className={styles.iconButton} tooltip='Card Styles' onClick={() => changeView('card-options')}>
        <i className={cx("cleverstory-card")} />
      </Property.Action>

      <Property.Divider />

      {totalPages > 1 && (
        <Pagination ref={paginationRef} active={!editMode && view === "pagination-options"} onPaginationClick={() => changeView('pagination-options')} editorSession={editorSession} {...props}></Pagination>
      )}

      <Property.Divider />

      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>


      {!editMode && view === "pagination-options" && (
        <Property.Dialog className={styles.styleDialog}
        width={264}
        target={paginationRef.current ? paginationRef.current : null}
        title={'Select Range:'}
        onCancel={closePopup}
        >
          <Property.Body>
            <div className={styles.paginationList}>
              {getPageRanges().map((range, index) => (
                <button className={cx(styles.paginationItem, { [styles.active]: currentPage === index})} onClick={() => onRangeClick(index)} key={index}>{range}</button>
              ))}
            </div>
          </Property.Body>
        </Property.Dialog>
      )}

      {!editMode && view === "spacing" && (
        <ContentSpacing
          id={id}
          widget={props}
          editorSession={editorSession}
          breakpoint={props.breakpoint}
          onCancel={closePopup}
        />
      )}

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          widgetProps={props}
          showShadow={true}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'fields' && (
        <LayoutFieldsDialog
          id={id}
          editorSession={editorSession}
          breakpoint={props.breakpoint}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      )}

      {!editMode && view === 'grid-options' &&
        <GridOptionDialog
          id={id}
          editorSession={editorSession}
          breakpoint={props.breakpoint}
          widget={props}
          changeView={changeView}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'custom-fields' &&
        <CreateFilter
          id={id}
          editorSession={editorSession}
          breakpoint={props.breakpoint}
          widget={props}
          changeView={changeView}
          create={true}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'card-options' &&
        <CardOptionDialog
          id={id}
          widget={props}
          editorSession={editorSession}
          breakpoint={props.breakpoint}
          onCancel={closePopup}
        />
      }

    </>
  )
}

const ContentWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const empty = _.get(props, 'content.mappings.items.length', 0) === 0;
  const loading = _.get(props, 'content.mappings.loading');

  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  if (empty && !loading) {
    return false;
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Fields' onClick={() => changeView('fields')}>
        <i className={cx("cleverstory-lines")} />
      </Property.Action>

      <Property.Divider />

      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'fields' && (
        <LayoutFieldsDialog
          id={id}
          editorSession={editorSession}
          breakpoint={props.breakpoint}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      )}
    </>
  )
}

const Pagination = React.forwardRef((props, ref) => {

  const dispatch = useDispatch();
  const editorSession = props.editorSession;
  const id = _.get(props, 'uid');

  const currentPage = _.get(props, 'content.mappings.pagination.current', 0);
  const totalPages = _.get(props, 'content.mappings.pagination.totalPages', 1);
  const limitPerPage = _.get(props, 'properties.desktop.layoutStyle.itemsPerPage', 2);
  const totalRecordsCount = _.get(props, 'content.mappings.pagination.totalRecordsCount', 0);

  const startItem = currentPage * limitPerPage + 1;
  const endItem = Math.min((currentPage + 1) * limitPerPage, totalRecordsCount);

  function goToPrevious(){
    dispatch(fetchMappingsForContentGrid(editorSession, id, currentPage-1, limitPerPage, false));
  }

  function goToNext(){
    dispatch(fetchMappingsForContentGrid(editorSession, id, currentPage+1, limitPerPage, false));
  }

  const handleTextClick = () => {
    if (props.onPaginationClick) {
      props.onPaginationClick();
    }
  }

  return (
    <div className={styles.sliderControl} ref={ref}>
      <Button className={styles.leftButton} disabled={currentPage === 0} variant='info' onClick={() => goToPrevious()}>
        <i className={cx('cleverstory-drop-down', styles.leftSide)} />
      </Button>

      <button className={cx(styles.action, { [styles.active]: props.active})} onClick={() => handleTextClick()}><span style={{ fontWeight: 500 }}>{startItem}-{endItem}</span>
        &nbsp; of &nbsp;
        <span style={{ fontWeight: 500 }}>{totalRecordsCount}</span>
      </button>
      <Button className={styles.rightButton} disabled={currentPage+1===totalPages} variant='info' onClick={() => goToNext()}>
        <i className={cx('cleverstory-drop-down', styles.rightSide)} />
      </Button>
    </div>
  )
})

const LayoutFieldsDialog = ({ editorSession, id, breakpoint, ...rest }) => {
  const dispatch = useDispatch();

  const widgets = useSelector(state =>  _.get(state, `${editorSession}.present.widgets`)) || [];
  
  const cardTemplate = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    return widgets.find(item => item.parent === id && item.type === CONTENT_CARD_TEMPLATE);
  });

  const customFields = useSelector(state => {
    const fields = _.get(state, `fields.items`) || [];
    return fields;
  });

  const basicFields = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    const _findFieldByName = (name, parent) => {
      const widget = widgets.find(item => item.uid === parent);
      if (!widget) {
        return null;
      }

      if (widget.parameter === name) {
        return widget;
      }

      return widgets.filter(item => item.parent === parent).find(item => {
        return _findFieldByName(name, item.uid);
      });
    }

    return fieldsTypes.map(field => {
      const widget = _findFieldByName(field.parameter, cardTemplate.uid);
      const visible = _.get(widget, `properties.${breakpoint}.visible`);

      return {
        ...field,
        widget: widget,
        visible: visible
      };
    });
  });

  const customWidgets = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    return widgets.filter(item => {
      return item.parent === cardTemplate.uid && item.parameter == null
    });
  });

  const handleBasicFieldClicked = (field) => {
    if (field.widget) {
      const visible = field.visible;
      dispatch(updateWidgetProperty(editorSession, field.widget.uid, breakpoint, {
        visible: !visible
      }));
    } else {
      dispatch(addFieldToContentGrid(editorSession, id, field, breakpoint));
    }
  }

  const handleDeleteCustomWidgetClicked = (widget) => {
    dispatch(removeWidgetFromContentGrid(editorSession, id, widget.uid));
  }

  const handleFieldClick = (field, visibility) => {
    const widget = _findWidgetByFieldId(field, cardTemplate.uid);
    let fieldRequest = null;

    if(!widget) {
      fieldRequest = {
        name: field.name,
        visible: visibility,
        parameter:  "custom_" + field.id,
        widgetConfig: {
          type: "cleverstory.v2.widgets.text.basic",
          width: 260,
          height: 24
        }
      };
      dispatch(addFieldToContentGrid(editorSession, id, fieldRequest, breakpoint));   
    } else {
      dispatch(updateWidgetProperty(editorSession, widget.uid, breakpoint, {
        visible: visibility
      }));
    }
  }

  const _findWidgetByFieldId = (field, parent) => {
    const widget = widgets.find(item => item.parent === parent && item.parameter === "custom_" + field.id);

    if (!widget) {
      return null;
    }
    return widget;
  }

  return (
    <Property.Dialog
      className={styles.fieldsDialog}
      width={230}
      target={`#${id}`}
      title={"Layout Fields"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <div className={styles.fields}>
          <div className={styles.group}>Basic Fields</div>
          {basicFields.map(field => {
            const visible = field.visible;

            return (
              <div className={styles.field}>
                <span className={styles.name}>
                  <Truncate lines={1}>{field.name}</Truncate>
                </span>
                <Tooltip className={styles.tooltip} placement='top' content={visible ? `Hide in card` : `Show in card`}>
                  <button className={styles.action} onClick={() => handleBasicFieldClicked(field)}>
                    <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
                  </button>
                </Tooltip>
              </div>
            );
          })}
        </div>
        {_.size(customWidgets) > 0 &&
          <div className={styles.fields}>
            <div className={styles.group}>Custom Widgets</div>
            {customWidgets.map(widget => {
              return (
                <div className={styles.field}>
                  <span className={styles.name}>
                    <Truncate lines={1}>{widget.name}</Truncate>
                  </span>
                  <Tooltip className={styles.tooltip} placement='top' content={`Remove from card`}>
                    <button className={styles.action} onClick={() => handleDeleteCustomWidgetClicked(widget)}>
                      <i className={cx(styles.icon, 'cleverstory-trash')} />
                    </button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        }
        {_.size(customFields) > 0 &&
          <div className={styles.fields}>
            <div className={styles.group}>Custom Fields</div>
            {customFields.map(customField => {
              const widget =  _findWidgetByFieldId(customField, cardTemplate.uid);
              const visible = _.get(widget, `properties.${breakpoint}.visible`) || false;

              return (
                <div className={styles.field}>
                  <TruncatedTextWithTooltip content={customField.name} className={styles.name} maxWidth={160} placement="top"/>
                  <Tooltip className={styles.tooltip} placement='top' content={visible ? `Hide in card` : `Show in card`}>
                    <button className={styles.action} onClick={() => handleFieldClick(customField, !visible)}>
                      <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
                    </button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        }
      </Property.Body>
    </Property.Dialog>
  );
}

const LayoutStyleDialog = ({ id, properties, breakpoint, editorSession, ...rest }) => {
  const dispatch = useDispatch();

  const handleChangeLayoutStyle = (layoutType) => {
    const layoutStyle = _.get(properties, `${breakpoint}.layoutStyle`, null);
    var request = {
      layoutStyle: {
        ...layoutStyle,
        type: layoutType,
      },
    };

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
  };

  return (
    <Property.Dialog
      width={230}
      target={`#${id}`}
      title={"Layout"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <div className={styles.layoutStyleEditor}>
          <button onClick={() => handleChangeLayoutStyle("grid")}> Grid</button>
          <button onClick={() => handleChangeLayoutStyle("metro")}> Metro </button>
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};

const ContentSpacing = ({ id, widget, breakpoint, editorSession, ...rest }) => {
  const dispatch = useDispatch();
  const storyboardContext = React.useContext(StoryboardContext);

  const storyboardType = _.get(storyboardContext, 'type');
  const layoutStyle = _.get(widget, `properties.${breakpoint}.layoutStyle`, null);
  const rowSpacing = _.get(widget, `properties.${breakpoint}.layoutStyle.rowSpacing`, 0);
  const columnSpacing = _.get(widget, `properties.${breakpoint}.layoutStyle.columnSpacing`, 0);
  const alignment = _.get(widget, `properties.${breakpoint}.layoutStyle.alignment`) || 'center';

  const margin = _.get(widget, `properties.${breakpoint}.margin`, 0);
  const stretched = _.get(widget, `properties.${breakpoint}.stretched`, false);
  const layout = _.get(widget, `properties.${breakpoint}.layout`);

  const pageWidget = usePageWidgetSelector(editorSession, id);
  const pageWidth = _.get(pageWidget, 'properties.desktop.container.minWidth');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, 'desktop', value));
  }

  const handleColumnSpacing = (value) => {
    var request = {
      layoutStyle: {
        ...layoutStyle,
        columnSpacing: value
      },
    };

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
  };

  const handleRowSpacing = (value) => {
    var request = {
      layoutStyle: {
        ...layoutStyle,
        rowSpacing: value
      },
    };

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
  };

  const handleMarginChange = (value) => {
    handlePropertyChange({
      margin: value
    });
  }

  const handleSectionSize = (stretched) => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    if (stretched) {
      frame.remove('width');
      frame.remove('height');
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    } else {
      frame.remove('height');
      frame.set('width', `${pageWidth}px`);
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    }

    handlePropertyChange({
      stretched: stretched,
      layout: frame.get()
    });
  }

  const handleAlignmentChange = (value) => {
    var request = {
      layoutStyle: {
        ...layoutStyle,
        alignment: value
      },
    };

    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
  }

  return (
    <Property.Dialog
      className={styles.contentSpacingDialog}
      width={230}
      target={`#${id}`}
      title={"Spacing"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Alignment" className={styles.alignmentProperty}>
          <div className={styles.options}>
            <button className={cx(styles.alignment,
              {
                [styles.active]: alignment === 'left'
              })}
              onClick={handleAlignmentChange.bind(this, 'left')}
            >
              <i className={cx('cleverstory-align-left')}></i>
            </button>
            <button className={cx(styles.alignment,
              {
                [styles.active]: alignment === 'center'
              })}
              onClick={handleAlignmentChange.bind(this, 'center')}
            >
              <i className={cx('cleverstory-align-center')}></i>
            </button>
            <button className={cx(styles.alignment,
              {
                [styles.active]: alignment === 'right'
              })}
              onClick={handleAlignmentChange.bind(this, 'right')}
            >
              <i className={cx('cleverstory-align-right')}></i>
            </button>
          </div>
        </Property>
        <Property name="Column Spacing">
          <Property.Slider
            input
            min={0}
            max={40}
            step={1}
            value={columnSpacing}
            onChange={handleColumnSpacing}
          />
        </Property>
        <Property name="Row Spacing">
          <Property.Slider
            input
            min={0}
            max={40}
            step={1}
            value={rowSpacing}
            onChange={handleRowSpacing}
          />
        </Property>
        {storyboardType != "INTERACTIVE_CONTENT" &&
          <>
            <Property name="Stretch to full width" className={styles.sectionSizeProperty}>
              <div className={styles.sectionSizeContainer}>
                <div className={cx(styles.sectionSize, styles.stretched, {
                  [styles.active]: stretched
                })} onClick={() => handleSectionSize(true)}>
                  <div className={styles.icon}>
                    <div className={styles.inner}></div>
                  </div>
                  <label>Screen</label>
                </div>
                <div className={cx(styles.sectionSize, {
                  [styles.active]: !stretched
                })} onClick={() => handleSectionSize(false)}>
                  <div className={styles.icon}>
                    <div className={styles.inner}></div>
                  </div>
                  <label>Page</label>
                </div>
              </div>
            </Property>
            {stretched &&
              <Property name="Margin Spacing" className={styles.marginProperty}>
                <Property.Slider
                  min={0}
                  max={190}
                  step={1}
                  input
                  value={margin}
                  onChange={handleMarginChange}
                />
              </Property>
            }
          </>
        }
      </Property.Body >
    </Property.Dialog >
  );
};

const GridOptionDialog = ({ id, widget, position, changeView, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');

  const properties = _.get(widget, `properties.desktop.layoutStyle`, {});
  const desktopProperties = _.get(widget, `properties.desktop`, {});
  const pagination = _.get(properties, 'pagination', 'infinite_scroll');
  const itemsPerPage = _.get(properties, 'itemsPerPage', 2);
  const filters = _.get(desktopProperties, 'filters', false);
  const search = _.get(desktopProperties, 'search', false);
  const editorState = _.get(widget, 'editorState', {});
  const renderState = _.get(editorState, 'renderState', 'default');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, 'desktop', value));
  }

  const handlePaginationChange = (value) => {
    handlePropertyChange({
      layoutStyle: {
        ...properties,
        pagination: value
      }
    })
  }

  const handleItemsPerPageChange = (value) => {
    handlePropertyChange({
      layoutStyle: {
        ...properties,
        itemsPerPage: value
      }
    })
  }

  const handleSearchFiltersToggle = (value, type) => {
    
    handlePropertyChange({
        ...desktopProperties,
        [type]: value
    })

    if(value && type == 'filters') {
      changeView('custom-fields');
    } else {
      handleSearchFilterState('default')
    }
  }

  const handleSearchFilterState = (value) => {
    let request = {
      ...editorState,
      renderState: value
    }

    dispatch(updateWidgetEditorState(editorSession, id, request));
  }

  return (
    <>
      <Property.Dialog className={styles.gridOptionDialog}
        width={246}
        target={`#${id}`}
        title={'Grid Option'}
        onCancel={rest.onCancel}
      >
        <div className={styles.body}>
          <h5>Pagination</h5>
          <div className={styles.pagniationOptions}>
            <Property.RadioButton title='Infinite Scroll' value={pagination === 'infinite_scroll'} onClick={() => handlePaginationChange('infinite_scroll')} />
            <Property.RadioButton title='Load More' value={pagination === 'load_more'} onClick={() => handlePaginationChange('load_more')} />
          </div>
          <div className={styles.rows}>
            <span className={styles.name}>No. of contents to load</span>
            <Property.Number className={styles.input} min={1} max={10} value={itemsPerPage} onChange={handleItemsPerPageChange} />
          </div >
          {storyboardType == "RESOURCE_CENTER" &&
            <>
              <h5>Search & Filters</h5>
              <div className={styles.filtersOptions}>                  
                  <Property.Switch
                    id="search"
                    title={'Search'}
                    value={search}
                    onChange={()=>handleSearchFiltersToggle(!search, 'search')}
                    className={styles.switchContainer}
                  />
              </div>
              {search &&
                <div className={cx(styles.assetCardStates)}>
                  <span onClick={()=>handleSearchFilterState('default')} className={cx(styles.option,{[styles.active ]: renderState === "default"})}>Default</span>
                  <span onClick={()=>handleSearchFilterState('loading')} className={cx(styles.option,{[styles.active ]: renderState === "loading"})}>Loading</span>
                  <span onClick={()=>handleSearchFilterState('noResult')} className={cx(styles.option,{[styles.active ]: renderState === "noResult"})}>No results</span>
                </div>
              }
              <div className={styles.filtersOptions}>                  
                  <Property.Switch
                    id="filters"
                    title={'Filters'}
                    value={filters}
                    onChange={()=>handleSearchFiltersToggle(!filters, 'filters')}
                    className={styles.switchContainer}
                  />
              </div>
              {filters &&
                <div className={cx(styles.assetCardStates)}>
                  <span onClick={()=>handleSearchFilterState('default')} className={cx(styles.option,{[styles.active ]: renderState === "default"})}>Default</span>
                  <span onClick={()=>handleSearchFilterState('loading')} className={cx(styles.option,{[styles.active ]: renderState === "loading"})}>Loading</span>
                  <span onClick={()=>handleSearchFilterState('noResult')} className={cx(styles.option,{[styles.active ]: renderState === "noResult"})}>No results</span>
                </div>
              }
            </>
          }
        </div >
      </Property.Dialog>
    </>
  );
}

const CardOptionDialog = ({ id, widget, breakpoint, editorSession, ...rest }) => {
  const [show, setShow] = useState(false);

  const toggleShow = () => {
    setShow(!show);
  }

  return (
    <Property.Dialog className={styles.cardOptionDialog}
      width={246}
      target={`#${id}`}
      title={'Changing card template'}
      onCancel={rest.onCancel}
    >
      <div className={styles.cardOptionConfirmation}>
        <p className={styles.confirmationText}>
          Changing the card template may affect the
          existing style and content within the card.
          Sure you want to continue?
        </p>

        <div className={styles.actions}>
          <Button variant='secondary' onClick={rest.onCancel} className={styles.button}>
            Cancel
          </Button>
          <Button variant='primary' onClick={toggleShow} className={styles.button}>
            Change
          </Button>
        </div>
      </div>
      <CardOptionModal
        id={id}
        widget={widget}
        editorSession={editorSession}
        show={show}
        onClose={toggleShow} />
    </Property.Dialog>
  )
}

const CardOptionModal = ({ id, editorSession, show, onClose }) => {
  const dispatch = useDispatch();
  const scrollContainerRef = React.useRef(null);

  const cards = useSelector(state => _.get(state, 'contentCards.items', []));
  const loading = useSelector(state => _.get(state, 'contentCards.loading', false));
  const pagination = useSelector(state => _.get(state, 'contentCards.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    if (show) {
      handleFetchMappings(0);
    }

    if (!show) {
      dispatch(invalidateContentCards());
    }
  }, [show])

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMappings(nextPage);
  }

  const handleFetchMappings = (page) => {
    dispatch(fetchContentCards(page, 20));
  }

  const handleCardClicked = (card) => {
    setSelectedCard(card);
    dispatch(changeContentGridCard(editorSession, id, card.id));
  }

  return (
    <Modal show={show} className={styles.cardOptionModal} backdropClassName={styles.backdrop} centered>
      <Modal.Header>
        <div className={styles.header}>
          <h1 className={styles.title}>Choose a card</h1>
          <button className={styles.closeButton}>
            <i className={cx('cleverstory-close', styles.closeButtonIcon)} onClick={onClose} />
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.cardGridContainer}>
        {loading && _.size(cards) === 0 &&
          <Loader className={styles.loading} title="Loading cards..." />
        }

        {selectedCard &&
          <div className={styles.processing}>
            <div className={styles.message}>Switching card...</div>
          </div>
        }

        <InfiniteScroll
          className={cx(styles.scrollable)}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={loadMore}
        >
          {cards.map((card, index) => {
            var imageWidth = _.get(card, 'icon.width') || 400;
            const imageHeight = _.get(card, 'icon.height') || 300;

            imageWidth = imageWidth * 266 / imageHeight;
            const colSpan = Math.ceil(imageWidth / 400);

            return (
              <ContentCard
                key={card.id}
                className={`x${colSpan}`}
                index={index + 1}
                card={card}
                onClick={handleCardClicked}
              />
            );
          })}
        </InfiniteScroll>
      </Modal.Body>
    </Modal>
  )
}

const ContentCard = ({ index, card, className, onClick }) => {
  const icon = _.get(card, 'icon.url', null);

  const handleClick = () => {
    if (onClick) {
      onClick(card);
    }
  }

  return (
    <div key={card.id} className={cx(styles.contentCardContainer, className)} onClick={handleClick}>
      <div className={styles.icon}>
        <IKImage
          path={icon || defaultContentImage}
          transformation={[{
            "width": "600"
          }]}
        />
      </div>
      <div className={styles.name}>{`${index}. ${card.name}`}</div>
    </div>
  )
}

ContentWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === "desktop" && (
        <ContentWidgetDesktopAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}
      {breakpoint === "mobile" && (
        <ContentWidgetMobileAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}
    </>
  );
};

ContentWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: false,
    rotatable: false,
    cloneable: false,
    deletable: true,
    renderDirections: [],
    onDrag: (layout) => {
      const frame = new Frame(layout);
      const translate = frame.get('transform', 'translate');
      const values = _.split(translate, ',');
      var y = parseInt(values[1]);
      y = Math.max(0, y);
      const newTranslate = `0px, ${y}px`;

      frame.set('transform', 'translate', newTranslate);

      return frame.get();
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.remove('height');
      return frame.get();
    },
    onReparent: (widget, parent) => {
      const parentWidgetType = getWidgetType(editorSession, parent);
      if(parentWidgetType === CAROUSEL_CARD) {
        return false;
      }

      if(parentWidgetType === SECTION_COLUMN) {
        return false;
      }
      return true;
    }
  };
};

const processLayout = (layout) => {
  const frame = new Frame(layout);
  frame.remove('height');
  frame.remove('width');
  return frame.get();
}

ContentWidget.processRequest = (widget) => {
  var widgetRequest = _.omit(widget, ['editorState']);

  const changes = _.get(widgetRequest, 'content.changes') || {};
  var processedChanges = Object.keys(changes).map(key => {
    const change = changes[key];
    const overrides = change.overrides || {};
    const updatedOverrides = Object.keys(overrides).map(name => {
      const override = overrides[name];
      var content = override.content;
      var contentType = null;

      if (name === 'icon') {
        content = _.get(content, 'id');
        contentType = 'image';
      } else if (_.get(content, 'type') === 'image') {
        content = _.get(content, 'id');
        contentType = 'image';
      } else if (_.get(content, 'type') === 'video') {
        content = _.get(content, 'id');
        contentType = 'video';
      }

      return {
        ...override,
        override: override.override,
        name: name,
        content: content,
        contentType: contentType,
      };
    });

    return {
      id: change.id,
      type: change.type,
      overrides: updatedOverrides
    }
  })

  var background = _.get(widget, 'properties.desktop.background', null);

  if (background) {
    var media = _.get(background, 'media');
    if (media) {
      background = {
        ...background,
        media: media.id
      }
    }
  }

  var desktopLayout = _.get(widgetRequest, 'properties.desktop.layout');
  var mobileLayout = _.get(widgetRequest, 'properties.mobile.layout');

  var processedDesktopLayout = processLayout(desktopLayout);
  var processedMobileLayout = processLayout(mobileLayout);

  return {
    ...widgetRequest,
    content: {
      id: _.get(widgetRequest, 'content.id', null),
      type: _.get(widgetRequest, 'content.type', null),
      changes: _.size(processedChanges) === 0 ? null : processedChanges
    },
    properties: {
      ...widget.properties,
      desktop: {
        ...(_.get(widget, 'properties.desktop') || {}),
        background: background,
        layout: processedDesktopLayout
      },
      mobile: {
        ...(_.get(widget, 'properties.mobile') || {}),
        layout: processedMobileLayout
      }
    }
  };
}

function getStyles(widgetProps, storyboardType, breakpoint) {
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false) && storyboardType != 'INTERACTIVE_CONTENT';
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const padding = _.get(widgetProps, `properties.${breakpoint}.padding`, 0);

  var widgetStyles = {
    marginLeft: 0,
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`,
    width: `375px`,
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      paddingLeft: `${padding}px`,
      paddingRight: `${padding}px`,
      width: '100%',
    }

    if (stretched) {
      widgetStyles = {
        ...widgetStyles,
        width: `calc(var(--editor-width) - 2 * ${margin}px)`,
        left: `calc((100% - (var(--editor-width) - 2 * ${margin}px))/2)`
      }
    }
  }

  //DropShadow
  const shadow = _.get(widgetProps, `properties.desktop.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  widgetStyles = {
    ...widgetStyles,
    ...shadowStyle
  }

  return widgetStyles;
}

function getButtonStyle(widgetProps, breakpoint) {
  var styles = {};

  const alignment = _.get(widgetProps, `properties.${breakpoint}.buttonAlignment`, {});

  var position = _.get(alignment, 'position', 'center');
  styles['justifyContent'] = position;
  styles['textAlign'] = position;

  var margin = _.get(alignment, 'margin', 0);
  if (position === 'left') {
    styles = {
      ...styles,
      paddingLeft: `${margin}px`
    }
  } else if (position === 'right') {
    styles = {
      ...styles,
      paddingRight: `${margin}px`
    }
  }

  return styles;
}

export { ContentWidget }
